import { Component, Input, OnInit, Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalSearchData, Vicinity } from '@vendasta/listing-products';
import { Geo } from '@vendasta/listing-products/lib/_internal/objects/listing-profile';
import { Location } from '@vendasta/listing-products';
import { GMBClaimStatus, Status } from '@vendasta/snapshot';
import { Observable, ReplaySubject } from 'rxjs';
import { SEOTableData } from '../../local-seo';
import { offsetToUTCZero } from '../local-seo-graph/local-seo-graph.component';
import { BusinessPerformance, MapMarker, SEOPerformance } from './local-seo-grid';
import { KeywordTrackingService } from '../../keyword-tracking-page/keyword-tracking/keyword-tracking.service';

@Pipe({
  name: 'ordinalNumber',
  standalone: false,
})
export class OrdinalNumberPipe implements PipeTransform {
  static nth(n: number): string {
    if (n > 3 && n < 21) return 'th';
    switch (n % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  transform(value: number): string {
    if (!value) {
      return '';
    }
    return `${value}${OrdinalNumberPipe.nth(value)}`;
  }
}

const selectedMarkerOpacity = 1;
const markerOpacity = 0.6;

@Component({
  selector: 'local-seo-grid',
  templateUrl: 'local-seo-grid.component.html',
  styleUrls: ['local-seo-grid.component.scss'],
  providers: [OrdinalNumberPipe],
  standalone: false,
})
export class LocalSEOGridComponent implements OnInit {
  @Input() accountGroupId: string;
  @Input() businessData: Location;
  @Input() localSEOData: SEOTableData;
  @Input() zoomValue: number;
  @Input() radiusSetting: number;

  private currentNearMeSearch$$: ReplaySubject<LocalSearchData> = new ReplaySubject<LocalSearchData>(1);
  private readonly conversionFactor = 1.60934;
  currentNearMeSearch$: Observable<LocalSearchData> = this.currentNearMeSearch$$.asObservable();
  LocalSearchData: LocalSearchData[];
  nearMeMarkers: MapMarker[];
  searchTerm: string;
  dataRadiusValue: number;
  mapUnits: string;
  translatedMapUnitsKMS: string;
  isloading = true; // Flag to track loading state

  public get SEOPerformance(): typeof SEOPerformance {
    return SEOPerformance;
  }

  public get Vicinity(): typeof Vicinity {
    return Vicinity;
  }

  public get Status(): typeof Status {
    return Status;
  }

  public get GMBClaimStatus(): typeof GMBClaimStatus {
    return GMBClaimStatus;
  }

  public get CenterPoint(): Geo {
    const center = this.LocalSearchData?.find((search) => search.vicinity === Vicinity.VICINITY_C3);
    if (!center) {
      return this.businessData.location;
    }

    return center?.searchLocation;
  }

  public get MainBusinessPerformance(): BusinessPerformance {
    const center = this.LocalSearchData?.find((search) => search.vicinity === Vicinity.VICINITY_C3);
    const mainBusiness = center?.results.find((result) => result.isMainBusiness);
    return this.businessPerformance(+mainBusiness.rank);
  }

  public get MainBusinessRank(): number {
    const center = this.LocalSearchData?.find((search) => search.vicinity === Vicinity.VICINITY_C3);
    const mainBusiness = center?.results.find((result) => result.isMainBusiness);
    return +mainBusiness.rank;
  }

  constructor(
    private ordinalPipe: OrdinalNumberPipe,
    private translate: TranslateService,
    private localSEOService: KeywordTrackingService,
  ) {}

  ngOnInit(): void {
    this.localSEOService.GetLocalSEOSearchDetails(this.localSEOData.keyword.value).subscribe((localSearches) => {
      this.LocalSearchData = localSearches;
      this.currentNearMeSearch$$.next(localSearches.find((search) => search.vicinity === Vicinity.VICINITY_C3));
      this.nearMeMarkers = this.buildMarkers(localSearches);
      this.isloading = false;
    });

    // this.radiusValue = Object.keys(SEODataSearchRadiusZoomValue).find((key) => SEODataSearchRadiusZoomValue[key] === this.zoomValue) ??
    //   SEARCH_RADIUS_LOCAL.toString();

    this.mapUnits =
      this.getLocalStorage('mapUnits') ?? this.translate.instant('LOCAL_SEO.SELECT_LOCAL_SEARCH_RADIUS.MILES');

    this.translate.get('LOCAL_SEO.SELECT_LOCAL_SEARCH_RADIUS.KMS').subscribe((translation) => {
      this.translatedMapUnitsKMS = translation;
    });
  }

  onMarkerClick(index: number): void {
    this.currentNearMeSearch$$.next(this.LocalSearchData[index]);
    this.setNearMeSelectedMarker(index);
  }

  offsetUTC(date: Date): Date {
    return offsetToUTCZero(date.toString());
  }

  businessPerformance(rank: number): BusinessPerformance {
    if (rank > 0 && rank <= 3) {
      return SEOPerformance.TOP;
    }
    if (rank > 3 && rank <= 20) {
      return SEOPerformance.AVERAGE;
    }
    if (rank > 20 && rank <= 100) {
      return SEOPerformance.BOTTOM;
    }
    return SEOPerformance.UNKNOWN;
  }

  getLocalStorage(key: string): string | null {
    return localStorage.getItem(`${this.accountGroupId}-${key}`);
  }

  buildMarkers(searches: LocalSearchData[]): MapMarker[] {
    const businessMarkers = [];

    searches.forEach((s) => {
      const markerBase = {
        path: 0,
        strokeWeight: 2,
        strokeColor: 'white',
        scale: 22,
        fillOpacity: this.setSelectedCenterMarker(s.vicinity),
      };

      const mainBusinessFiltered = s.results?.filter((b) => b.isMainBusiness);
      const mainBusiness = mainBusinessFiltered.length > 0 ? mainBusinessFiltered[0] : null;
      if (mainBusiness) {
        const markerIcon = {
          ...markerBase,
          fillColor: this.businessPerformance(+mainBusiness.rank).color,
        };

        if (s.searchLocation) {
          businessMarkers.push({
            label: {
              text: mainBusiness?.rank ? this.ordinalPipe.transform(+mainBusiness.rank) : '-',
              color: 'white',
            },
            lat: s.searchLocation.latitude,
            lng: s.searchLocation.longitude,
            icon: markerIcon,
          });
        }
      } else {
        businessMarkers.push({
          label: {
            text: '-',
            color: 'white',
          },
          lat: s.searchLocation.latitude,
          lng: s.searchLocation.longitude,
          icon: {
            ...markerBase,
            fillColor: SEOPerformance.UNKNOWN.color,
          },
        });
      }
    });
    return businessMarkers;
  }

  setSelectedCenterMarker(vicinity: Vicinity): number {
    if (vicinity === Vicinity.VICINITY_CITY || vicinity === Vicinity.VICINITY_C3) {
      return selectedMarkerOpacity;
    }
    return markerOpacity;
  }

  setNearMeSelectedMarker(index: number): void {
    this.nearMeMarkers.forEach((m) => {
      if (m.icon.fillOpacity === 1) {
        m.icon = { ...m.icon, fillOpacity: markerOpacity };
      }
    });
    this.nearMeMarkers[index].icon = { ...this.nearMeMarkers[index].icon, fillOpacity: selectedMarkerOpacity };
  }

  convertStringToNumber(radiusValue: string): number {
    return +radiusValue;
  }

  getConvertedRadiusValue(radiusValue: number, mapUnits: string, kilometersTranslationKey: string): number {
    if (mapUnits === kilometersTranslationKey) {
      const kilometers = radiusValue * this.conversionFactor;
      return Math.round(kilometers);
    }

    return radiusValue;
  }
}
